import * as React from "react";
import Drawer from "@mui/material/Drawer";
import {CircularProgress} from "@mui/material";
import DownloadIcon from "@mui/icons-material/CloudDownload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {useContext, useEffect, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {UIStoreContext} from "../../context";
import {observer} from "mobx-react-lite";
import styles from "./UpdatesDrawer.module.scss";
import {DateTime} from "luxon";
import closeIcon from "./close-small.svg";
import mainlineImage from "../deviceItem/mainline.png";
import switchYardImage from "../deviceItem/sy.png";
import updatesTable, {SoftwareUpdate} from "../../client-api/services/updatesTable";
import actionsService from "../../client-api/services/actions";
import {SystemType} from "../../common/systemType";

type DrawerTab = "general" | "configuration" | "softwareUpdate" | "logs";

const TABS: {
    id: DrawerTab;
    label: string;
    Icon: typeof InfoOutlinedIcon;
    showDot?: boolean;
}[] = [
    {id: "general", label: "General", Icon: InfoOutlinedIcon},
    {id: "configuration", label: "Configuration", Icon: SettingsOutlinedIcon},
    {id: "softwareUpdate", label: "Software Update", Icon: CloudDownloadOutlinedIcon, showDot: true},
    {id: "logs", label: "Logs", Icon: DescriptionOutlinedIcon},
];

function extractVersionNumber(blobName: string): string {
    const match = blobName.match(/\d+\.\d+\.\d+/);
    if (match) {
        return match[0];
    }
    const shortMatch = blobName.match(/\d+\.\d+/);
    return shortMatch ? shortMatch[0] : "—";
}

type VersionStatus = { update_status?: number | string };

type UpdateActionResponse = {
    ok?: string;
    status?: number;
    file_name?: string;
};

function getUpdateStatusClass(
    canDownload: boolean,
    isDownloading: boolean,
    versionData?: VersionStatus
): string {
    if (isDownloading) {
        return styles.downloadBtnDisabled;
    }

    const status = Number(versionData?.update_status);
    if (Number.isFinite(status) && status === 10) {
        return styles.updateStatusPending;
    }
    if (Number.isFinite(status) && (status === 30 || status === 20)) {
        return styles.updateStatusDownloaded;
    }

    if (canDownload) {
        return styles.downloadBtn;
    }

    return styles.downloadBtnDisabled;
}

function UpdatesDrawer() {
    const uiStore = useContext(UIStoreContext);
    const queryClient = useQueryClient();
    const drawerData = uiStore.getUpdatesDrawerData();
    const isOpen = uiStore.getUpdatesDrawerOpen();
    const updateActionMutation = actionsService.UpdateSystem();
    const availableSoftwareUpdates = updatesTable.GetAvailableSoftwareUpdates(drawerData.device_type, drawerData.system_id, isOpen);
    const [activeTab, setActiveTab] = useState<DrawerTab>("softwareUpdate");
    const [confirmedVersions, setConfirmedVersions] = useState<Set<string>>(new Set());
    const [localVersionStatus, setLocalVersionStatus] = useState<Record<string, VersionStatus>>({});
    const [downloadingVersion, setDownloadingVersion] = useState<string | null>(null);

    const deviceImage = drawerData.device_type === SystemType.SwitchYard.valueOf()
        ? switchYardImage
        : mainlineImage;

    function resetDrawerState() {
        setActiveTab("softwareUpdate");
        setConfirmedVersions(new Set());
        setLocalVersionStatus({});
        setDownloadingVersion(null);
    }

    function cleanupDrawerResources() {
        updateActionMutation.reset();
        queryClient.removeQueries({queryKey: ["updateAction"], exact: false});
        resetDrawerState();
    }

    function closeDrawer() {
        cleanupDrawerResources();
        uiStore.closeUpdatesDrawer();
    }

    useEffect(() => {
        if (!isOpen) {
            cleanupDrawerResources();
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            resetDrawerState();
        }
    }, [isOpen, drawerData.system_id]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeDrawer();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, uiStore]);

    const toggleDrawer = (event: React.KeyboardEvent | React.MouseEvent) => {
        if (
            event.type === "keydown" &&
            ((event as React.KeyboardEvent).key === "Tab" ||
                (event as React.KeyboardEvent).key === "Shift")
        ) {
            return;
        }

        if (isOpen) {
            closeDrawer();
            return;
        }

        uiStore.setUpdatesDrawerOpen(true);
    };

    function parseDate(dateValue: string) {
        return dateValue !== ""
            ? DateTime.fromMillis(Number(dateValue) * 1000).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
            : "--";
    }

    function parseIsoDate(dateValue: string) {
        if (!dateValue) {
            return "—";
        }
        const parsed = DateTime.fromISO(dateValue);
        return parsed.isValid
            ? parsed.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
            : "—";
    }

    function handleDownload(version: string) {
        const formData = new FormData();
        formData.append("system_id", drawerData.system_id);
        formData.append("file_name", version);
        formData.append("csrf_token", uiStore.getCsrfToken());
        setDownloadingVersion(version);
        updateActionMutation.mutate(formData, {
            onSuccess: (data) => {
                if (!uiStore.getUpdatesDrawerOpen()) {
                    return;
                }
                const response = data as UpdateActionResponse;
                const fileName = response?.file_name ?? version;
                setLocalVersionStatus((prev) => ({
                    ...prev,
                    [fileName]: {update_status: response?.status ?? 10},
                }));
                setConfirmedVersions((prev) => {
                    const next = new Set(prev);
                    next.delete(fileName);
                    return next;
                });
            },
            onSettled: () => {
                if (!uiStore.getUpdatesDrawerOpen()) {
                    return;
                }
                setDownloadingVersion(null);
            },
        });
    }

    function renderGeneralTab() {
        return (
            <div className={styles.tabContent}>
                <div className={styles.sectionTitle}>Overview</div>
                <div className={styles.detailRows}>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Organization</span>
                        <span className={styles.detailValue}>{drawerData.device_org || "—"}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>System ID</span>
                        <span className={styles.detailValue}>{drawerData.system_id}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Device Type</span>
                        <span className={styles.detailValue}>{SystemType[drawerData.device_type]}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Status</span>
                        <span className={styles.detailValue}>{drawerData.isOnline ? "Ready" : "Offline"}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Last seen</span>
                        <span className={styles.detailValue}>{parseDate(drawerData.last_active)}</span>
                    </div>
                </div>
            </div>
        );
    }

    function renderSoftwareUpdateTab() {
        const updates: SoftwareUpdate[] = availableSoftwareUpdates.data ?? [];
        const currentVersion = drawerData.current_version;
        const isLoadingUpdates = availableSoftwareUpdates.isLoading
            || (availableSoftwareUpdates.isFetching && updates.length === 0);
        const hasError = availableSoftwareUpdates.isError;

        return (
            <div className={styles.tabContent}>
                <div className={styles.sectionTitle}>Software update is required</div>
                <div className={styles.updateCards}>
                    {isLoadingUpdates && (
                        <div className={styles.updatesLoading}>
                            <CircularProgress size={40} thickness={2} sx={{color: "#201d20"}}/>
                            <span>Loading software updates...</span>
                        </div>
                    )}
                    {!isLoadingUpdates && hasError && (
                        <div className={styles.emptyUpdates}>Failed to load software updates</div>
                    )}
                    {!isLoadingUpdates && !hasError && updates.length === 0 && (
                        <div className={styles.emptyUpdates}>No software updates available</div>
                    )}
                    {!isLoadingUpdates && !hasError && updates.map((update) => {
                        const version = update.name;
                        const isCurrentVersion = currentVersion !== "" && version === currentVersion;
                        const isConfirmed = confirmedVersions.has(version);
                        const canDownload = isConfirmed && !isCurrentVersion;
                        const currentStatus: VersionStatus = {
                            ...update.version_data,
                            ...localVersionStatus[version],
                        };
                        const status = Number(currentStatus?.update_status);
                        const isPending = Number.isFinite(status) && status === 10;
                        const isDownloadingThis = downloadingVersion === version;

                        const toggleConfirmed = () => {
                            setConfirmedVersions((prev) => {
                                const next = new Set(prev);
                                if (next.has(version)) {
                                    next.delete(version);
                                } else {
                                    next.add(version);
                                }
                                return next;
                            });
                        };

                        return (
                            <div className={styles.updateCard} key={version}>
                                <div className={styles.updateCardTitle}>
                                    {version}
                                </div>
                                <div className={styles.updateCardMeta}>
                                    <div className={styles.metaRow}>
                                        <span className={styles.metaLabel}>Version</span>
                                        <span className={styles.metaValue}>{extractVersionNumber(version)}</span>
                                    </div>
                                    <div className={styles.metaRow}>
                                        <span className={styles.metaLabel}>Release Date</span>
                                        <span className={styles.metaValue}>{parseIsoDate(update.last_modified)} </span>
                                    </div>
                                    <div className={styles.metaRow}>
                                        <span className={styles.metaLabel}>Product</span>
                                        <span className={styles.metaValue}>
                                            {drawerData.device_type === SystemType.SwitchYard.valueOf() ? "SY" : "ML"}
                                        </span>
                                    </div>
                                    <div className={styles.metaRow}>
                                        <span className={styles.metaLabel}>Customer</span>
                                        <span className={styles.metaValue}>{drawerData.device_org || "—"}</span>
                                    </div>
                                </div>

                                {!isCurrentVersion && !isPending && (
                                    <label className={styles.confirmCheckbox}>
                                        <input
                                            type="checkbox"
                                            checked={isConfirmed}
                                            onChange={toggleConfirmed}
                                        />
                                        Check to Confirm Download
                                    </label>
                                )}

                                <button
                                    className={getUpdateStatusClass(canDownload, isDownloadingThis, currentStatus)}
                                    disabled={!canDownload || isDownloadingThis || isPending}
                                    onClick={() => handleDownload(version)}
                                >
                                    {isDownloadingThis ? (
                                        <CircularProgress size={18} thickness={4} sx={{color: "#fff"}}/>
                                    ) : (
                                        <DownloadIcon fontSize="small"/>
                                    )}
                                    {isDownloadingThis
                                        ? "Downloading..."
                                        : `Download${currentStatus?.update_status != null ? ` ${currentStatus.update_status}` : ""}`}
                                </button>

                                {isCurrentVersion && (
                                    <div className={styles.warningText}>
                                        The selected version is identical to the current version already installed.
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    function renderPlaceholderTab(label: string) {
        return (
            <div className={styles.tabContent}>
                <div className={styles.placeholderText}>{label} content coming soon.</div>
            </div>
        );
    }

    function renderTabContent() {
        switch (activeTab) {
            case "general":
                return renderGeneralTab();
            case "configuration":
                return renderPlaceholderTab("Configuration");
            case "softwareUpdate":
                return renderSoftwareUpdateTab();
            case "logs":
                return renderPlaceholderTab("Logs");
        }
    }

    return (
        <Drawer
                className={styles.UpdatesDrawer}
                anchor="right"
                hideBackdrop
                variant="persistent"
                open={isOpen}
                onClose={toggleDrawer}
            >
                <div className={styles.imageClass}>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={(event) => toggleDrawer(event)}
                        aria-label="Close drawer"
                    >
                        <img className={styles.closeIcon} src={closeIcon} alt="close"/>
                    </button>
                </div>

                <div className={styles.headerInfo}>

                    <div className={styles.headerTop}>
                        <span
                            className={
                                drawerData.isOnline
                                    ? styles.statusDotOnline
                                    : styles.statusDotOffline
                            }
                        />
                        <div className={styles.headerText}>
                            <div className={styles.deviceName}>
                                {(drawerData.loco_name || drawerData.system_id).toUpperCase()}
                            </div>
                            <div className={styles.currentVersion}>
                                Current Version: {drawerData.current_version || "—"}
                            </div>
                        </div>
                    </div>
                    <img
                        className={styles.deviceImage}
                        src={deviceImage}
                        alt={drawerData.loco_name || "Device"}
                    />
                </div>

                <div className={styles.tabs}>
                    {TABS.map((tab) => {
                        const TabIcon = tab.Icon;
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ""}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <TabIcon className={styles.tabIcon} fontSize="small"/>
                                <span className={styles.tabLabel}>
                                    {tab.label}
                                    {tab.showDot && activeTab !== tab.id && (
                                        <span className={styles.tabDot}/>
                                    )}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className={styles.contentArea}>
                    {isOpen && renderTabContent()}
                </div>
            </Drawer>
    );
}

export default observer(UpdatesDrawer);
